import { Hidden } from '@ahmdigital/ui';
import { toUpper, upperFirst } from 'lodash/fp';
import { useFormikContext } from 'formik';
import Icon from '@ahmdigital/icons-web';
import moment from 'moment';
import React, { useEffect, useRef, useState } from 'react';

import { DEFAULT_DATE_FORMAT, FIELD_NAMES } from '../../../../constants';
import {
  ErrorText,
  FieldWrapper,
  IconWrapper,
  InnerInputWrapper,
  InputWrapper,
  Label,
  ModalWrapper,
  PlaceholderText,
  StyledButton,
  ToggleWrapper,
  Wrapper,
} from './styles';
import { FormValues } from '../../types';
import analytics from '../../../../../../../../analytics';
import AutoCompleteInput from '../auto-complete-input';
import constants from '../../../../../../../../ahm-constants';
import getPanelIndexForError from '../../utils/get-panel-index-for-error';
import useTravelWidgetContext from '../../hooks/use-travel-widget-context';

export type ModalComponentProps = {
  autoCompleteRef?: React.RefObject<HTMLDivElement>;
  setIsModalOpen?: (value: boolean) => void;
  setFieldTouched?: (field: string, value?: boolean, shouldValidate?: boolean) => void;
};

export type StepInputProps = {
  handleCloseWidgetOnTab: (event: React.KeyboardEvent<HTMLButtonElement>) => void;
  hasButton?: boolean;
  icon: string;
  index: number;
  isFirst?: boolean;
  isLast?: boolean;
  isModalOpen?: boolean;
  modalComponent?: React.FC<ModalComponentProps>;
  name: keyof FormValues;
  onMouseEnter: () => void;
  onMouseLeave: () => void;
  placeholder: string;
  setIsModalOpen: (value: boolean) => void;
  setIsWidgetActive: (value: boolean) => void;
};

const StepInput = ({
  handleCloseWidgetOnTab,
  hasButton = false,
  icon,
  index,
  isFirst,
  isLast,
  isModalOpen,
  modalComponent: ModalComponent,
  name,
  onMouseEnter,
  onMouseLeave,
  placeholder,
  setIsModalOpen,
}: StepInputProps) => {
  const sharedInputProps = {
    isLast,
    label: `${upperFirst(name)}*`,
    name,
    placeholder,
  };
  const { travellerCount } = useTravelWidgetContext();
  const sectionRef = useRef<HTMLDivElement>(null);
  const modalContentRef = useRef<HTMLDivElement>(null);
  const autoCompleteRef = useRef<HTMLDivElement>(null);

  const [isSuggestionsOpen, setIsSuggestionsOpen] = useState(false);
  const [hasError, setHasError] = useState(false);
  const [isButtonHovered, setIsButtonHovered] = useState(false);
  const { handleSubmit, errors, isSubmitting, values, setFieldTouched, submitCount } = useFormikContext<FormValues>();
  const errorText = errors[name as keyof FormValues];
  const showErrorText = errorText && isModalOpen && hasError;
  const formatDate = (date: string) =>
    moment(date, constants.DATE_FORMAT.STANDARD).format(toUpper(DEFAULT_DATE_FORMAT));

  const getWhenValue = () =>
    `${values.departureDate ? formatDate(values.departureDate) : DEFAULT_DATE_FORMAT} - ${values.returnDate ? formatDate(values.returnDate) : DEFAULT_DATE_FORMAT} `;

  const totalNumberOfTravellers = values.travelerAge_1 ? travellerCount + 1 : 0;
  const getWhoValue = () =>
    totalNumberOfTravellers
      ? `${totalNumberOfTravellers} traveller${totalNumberOfTravellers > 1 ? 's' : ''}`
      : 'Traveller details';

  const firstErrorPanelIndex = getPanelIndexForError({ errors });
  useEffect(() => {
    if (firstErrorPanelIndex === null) {
      setHasError(false);
      return;
    }
    const isCurrentPanel = firstErrorPanelIndex === index && !!isModalOpen;
    if (submitCount > 0) {
      setHasError(isCurrentPanel);
      if (isCurrentPanel) {
        sectionRef.current?.focus();
      }
    }
  }, [firstErrorPanelIndex, isSubmitting, isModalOpen]);

  const handleFocus = () => {
    setIsModalOpen(true);
  };
  const handleClick = () => {
    autoCompleteRef.current?.focus();
    analytics.publish('action', {
      action: 'Clicked',
      component: 'Element',
      label: `Get a quote ${name}`,
    });
  };

  return (
    <Wrapper isLast={isLast} onClick={handleClick}>
      <FieldWrapper
        hasError={hasError}
        isFirst={isFirst}
        isLast={isLast}
        isOpen={isModalOpen || isSuggestionsOpen}
        isButtonHovered={isButtonHovered}
        onFocus={handleFocus}
        onBlur={() => {
          setIsSuggestionsOpen(false);
        }}
        tabIndex={0}
        hasButton={hasButton}
        onMouseEnter={onMouseEnter}
        onMouseLeave={onMouseLeave}
        ref={sectionRef}
      >
        <InputWrapper
          hasError={hasError}
          isOpen={isModalOpen || isSuggestionsOpen}
          isFirst={isFirst}
          isLast={isLast}
          hasButton={hasButton}
        >
          <InnerInputWrapper>
            {name === FIELD_NAMES.WHERE ? (
              <AutoCompleteInput
                setIsModalOpen={setIsModalOpen}
                modalContentRef={modalContentRef}
                setIsSuggestionsOpen={setIsSuggestionsOpen}
                autoCompleteRef={autoCompleteRef}
                {...sharedInputProps}
              />
            ) : (
              <ToggleWrapper isLast={isLast}>
                <Label>{upperFirst(name)}*</Label>
                <PlaceholderText hasValue={name === FIELD_NAMES.WHEN ? values?.departureDate : values?.travelerAge_1}>
                  {name === FIELD_NAMES.WHEN ? getWhenValue() : getWhoValue()}
                </PlaceholderText>
              </ToggleWrapper>
            )}
            <IconWrapper>
              <Icon name={icon} size="medium" />
            </IconWrapper>
          </InnerInputWrapper>
          <Hidden xs sm md lg>
            {showErrorText && <ErrorText>{errorText}</ErrorText>}
          </Hidden>
        </InputWrapper>
      </FieldWrapper>

      {isSuggestionsOpen && !isModalOpen && (
        <ModalWrapper isFirst={isFirst} isLast={isLast} hasError={hasError} isOpen={isSuggestionsOpen}>
          <div ref={modalContentRef} />
        </ModalWrapper>
      )}

      {isModalOpen && !isSuggestionsOpen && ModalComponent && (
        <ModalWrapper isFirst={isFirst} isLast={isLast} hasError={hasError} isOpen={isModalOpen}>
          <Hidden df xl>
            {showErrorText && <ErrorText>{errorText}</ErrorText>}
          </Hidden>
          <ModalComponent
            autoCompleteRef={autoCompleteRef}
            setIsModalOpen={setIsModalOpen}
            setFieldTouched={setFieldTouched}
          />
        </ModalWrapper>
      )}
      {hasButton && (
        <Hidden xs sm md>
          <StyledButton
            icon={{
              name: 'arrow-right-long',
              position: 'right',
              size: 'large',
            }}
            onClick={handleSubmit}
            variant="flair"
            onMouseEnter={() => {
              setIsButtonHovered(true);
              onMouseEnter();
            }}
            onMouseLeave={() => setIsButtonHovered(false)}
            isLoading={isSubmitting}
            onKeyDown={handleCloseWidgetOnTab}
          >
            <Hidden lg>Get quote</Hidden>
          </StyledButton>
        </Hidden>
      )}
    </Wrapper>
  );
};

export default StepInput;
